import type {CSSProperties} from 'react';
import {wedgeExactRole, wedgeRole} from './wedgeRoles.js';

export type WedgeBriefPocketLook =
  | 'briefpocket-back' | 'briefpocket-toggle' | 'briefpocket-toggle-amber' | 'briefpocket-toggle-accent' | 'briefpocket-expand'
  | 'briefpocket-collapse' | 'briefpocket-intent' | 'briefpocket-intent-on' | 'briefpocket-more';

export const isWedgeBriefPocketLook = (look: string | undefined): look is WedgeBriefPocketLook => look !== undefined && look.startsWith('briefpocket-');

/* Released pocket brief (Wedge, the one screen hero of a real brief): the controls of the top row, the Collapse back chip of the expanded
   dossier, the intent chips and the More controls link. Every control is a small mono caps label: 9.5px at 700 weight and 0.08 tracking
   (Back is 10.5px at 0.06), on the tap highlight reset and manipulation touch action. Each look keeps the declaration order of the source so
   the shipped ink rules keep matching the serialized text, and the roles keep the legacy variable names. The caller owns the labels, the
   glyphs and the handlers.
   Back is a bare 40px text control. Toggle is the 0.5px separator outline chip (Honest, Rehearse, Listen) with an 8px margin on the right;
   toggle-amber and toggle-accent are its pressed fills (Honest on, Listen on). Expand is the same chip at 8px by 12px with the 1px card
   border and no margin. Collapse is the 32px chip of the expanded dossier. Intent is the 34px chip that does not shrink or wrap, and
   intent-on its selected fill. More is the full width centered link above the secondary controls.
   No look draws a transparent target and no anchor (`wedgeSettingsDrawsHit`, and a `content: none` rule naming the prefix): the legacy
   controls drew none, and the row and the chips sit in padded rows where a grown region would overlap their neighbors. */
const mono = () => wedgeRole('mono', "ui-monospace, 'Share Tech Mono', monospace");
const MONO_LABEL = {fontSize: 9.5, fontWeight: 700, letterSpacing: 0.08, textTransform: 'uppercase'} as const;
const TAP = {WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation'} as const;

export function wedgeBriefPocketStyle(look: WedgeBriefPocketLook): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'briefpocket-back') return {
    padding: '8px 10px', background: 'transparent', border: 0, color: r('labelSec'), cursor: 'pointer', minHeight: 40,
    fontFamily: mono(), fontSize: 10.5, fontWeight: 700, letterSpacing: 0.06, textTransform: 'uppercase', ...TAP,
  };
  if (look === 'briefpocket-toggle' || look === 'briefpocket-toggle-amber' || look === 'briefpocket-toggle-accent') {
    const tone = look === 'briefpocket-toggle-amber' ? 'amber' : look === 'briefpocket-toggle-accent' ? 'accent' : null;
    return {
      padding: '8px 10px', background: tone ? r(tone === 'amber' ? 'amberSoft' : 'accentSoft') : 'transparent',
      border: tone ? `0.5px solid ${r(tone)}` : `0.5px solid ${r('separator')}`, borderRadius: 8,
      color: tone ? r(tone) : r('labelSec'), cursor: 'pointer', minHeight: 40, marginRight: 8,
      fontFamily: mono(), ...MONO_LABEL, display: 'flex', alignItems: 'center', gap: 6, ...TAP,
    };
  }
  if (look === 'briefpocket-expand') return {
    padding: '8px 12px', background: 'transparent', border: `1px solid ${r('cardBorder')}`, borderRadius: 8,
    color: r('labelSec'), cursor: 'pointer', minHeight: 40,
    fontFamily: mono(), ...MONO_LABEL, display: 'flex', alignItems: 'center', gap: 6, ...TAP,
  };
  if (look === 'briefpocket-collapse') return {
    padding: '6px 10px', background: 'transparent', border: `1px solid ${r('cardBorder')}`, borderRadius: 8,
    color: r('labelSec'), cursor: 'pointer', minHeight: 32,
    fontFamily: mono(), ...MONO_LABEL, display: 'flex', alignItems: 'center', gap: 6, ...TAP,
  };
  if (look === 'briefpocket-intent' || look === 'briefpocket-intent-on') {
    const on = look === 'briefpocket-intent-on';
    return {
      padding: '7px 11px', background: on ? r('accentSoft') : 'transparent',
      border: on ? `0.5px solid ${r('accent')}` : `0.5px solid ${r('separator')}`, borderRadius: 8,
      color: on ? r('accent') : r('labelSec'), cursor: 'pointer', minHeight: 34,
      fontFamily: mono(), ...MONO_LABEL, whiteSpace: 'nowrap', flexShrink: 0, ...TAP,
    };
  }
  return {
    width: '100%', padding: '8px 0', marginBottom: 14, background: 'transparent', border: 0, cursor: 'pointer',
    fontFamily: mono(), ...MONO_LABEL, color: r('labelTer'),
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, ...TAP,
  };
}
